import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import logo from './../assets/logo.svg';
import defaultPlaceholder from './../assets/placeholder.png';
import ApiService from './../services/ApiService';
import ErrorCodes from '../services/ErrorCodes';
import CaptchaModal from '../components/CaptchaModal';
import {
    requestCaptcha,
    verifyPassiveCaptcha,
    verifyVisualCaptcha,
} from '../services/CaptchaService';

const RegisterPage = () => {
    const [placeholderImage, setPlaceholderImage] = useState(defaultPlaceholder);
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [hasProblem, setHasProblem] = useState(false);
    const [problemText, setProblemText] = useState('');
    const [isAgreed, setIsAgreed] = useState(false);
    const [captcha, setCaptcha] = useState(null);
    const [captchaInput, setCaptchaInput] = useState('');
    const [captchaLoading, setCaptchaLoading] = useState(false);
    const [captchaModalOpen, setCaptchaModalOpen] = useState(false);
    const [captchaError, setCaptchaError] = useState('');
    const [humanCheckState, setHumanCheckState] = useState('idle');
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const abortRef = useRef(null);
    const challengeLoadRef = useRef(null);
    const verifiedTokenRef = useRef(null);
    const captchaRef = useRef(null);
    const honeypotRef = useRef('');
    const submitAfterChallengeRef = useRef(false);
    const humanCheckPromiseRef = useRef(null);

    const rememberChallenge = (challenge) => {
        captchaRef.current = challenge;
        setCaptcha(challenge);
        setCaptchaInput('');
        setCaptchaError('');
        return challenge;
    };

    const fetchChallenge = async (signal) => {
        setCaptchaLoading(true);
        try {
            return rememberChallenge(await requestCaptcha(signal));
        } finally {
            setCaptchaLoading(false);
        }
    };

    const startChallengeLoad = () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const loading = fetchChallenge(controller.signal).catch((error) => {
            if (error.name !== 'AbortError') {
                console.error('Error loading captcha:', error);
                setCaptchaError('Проверка временно недоступна');
            }
            return null;
        });
        challengeLoadRef.current = loading;
        loading.finally(() => {
            if (challengeLoadRef.current === loading) challengeLoadRef.current = null;
        });
        return loading;
    };

    useEffect(() => {
        document.title = 'Регистрация в animix';
        const randomImageNumber = Math.floor(Math.random() * 3) + 1;
        try {
            setPlaceholderImage(require(`./../assets/back_${randomImageNumber}.png`));
        } catch (error) {
            console.error('Error loading random placeholder image:', error);
        }

        startChallengeLoad();
        return () => abortRef.current?.abort();
        // This preparation intentionally runs once when the registration form opens.
        // eslint-disable-next-line
    }, []);

    const currentVerifiedToken = () => {
        const verified = verifiedTokenRef.current;
        if (!verified || verified.expiresAt <= Date.now() + 5000) {
            verifiedTokenRef.current = null;
            if (verified) {
                captchaRef.current = null;
                setCaptcha(null);
                setHumanCheckState('idle');
            }
            return null;
        }
        return verified.token;
    };

    const performRegistration = async (captchaToken) => {
        try {
            const response = await ApiService.registerUser(login, password, 121212, captchaToken);
            if (response.type === 'success') {
                localStorage.setItem('sessionKey', response.data.key);
                navigate('/');
                return;
            }
            setHasProblem(true);
            setProblemText(ErrorCodes.getText(response));
        } catch (error) {
            console.error('Error registering user:', error.message);
            setHasProblem(true);
            setProblemText(`Неизвестная ошибка. ${error.message}`);
        }
        verifiedTokenRef.current = null;
        setHumanCheckState('idle');
        startChallengeLoad();
    };

    const performHumanCheck = async () => {
        const existingToken = currentVerifiedToken();
        if (existingToken) return existingToken;

        setHumanCheckState('checking');
        setCaptchaError('');
        try {
            let challenge = captchaRef.current;
            if (!challenge && challengeLoadRef.current) challenge = await challengeLoadRef.current;
            if (!challenge) challenge = await startChallengeLoad();
            if (!challenge) {
                setHumanCheckState('idle');
                return null;
            }

            if (challenge.interactionRequired !== false) {
                setHumanCheckState('idle');
                setCaptchaModalOpen(true);
                return null;
            }

            const controller = new AbortController();
            abortRef.current = controller;
            const accepted = await verifyPassiveCaptcha(challenge, honeypotRef.current, controller.signal);
            if (!accepted) {
                await fetchChallenge(controller.signal);
                setHumanCheckState('idle');
                setCaptchaModalOpen(true);
                return null;
            }

            const expiresAt = Date.now() + ((challenge.expiresInSeconds || 300) * 1000);
            verifiedTokenRef.current = { token: challenge.token, expiresAt };
            captchaRef.current = null;
            setCaptcha(null);
            setHumanCheckState('verified');
            return challenge.token;
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error checking captcha:', error);
                setCaptchaError('Не удалось выполнить проверку. Нажмите ещё раз');
            }
            setHumanCheckState('idle');
            return null;
        }
    };

    const handleHumanCheck = () => {
        if (humanCheckPromiseRef.current) return humanCheckPromiseRef.current;
        const verification = performHumanCheck();
        humanCheckPromiseRef.current = verification;
        verification.finally(() => {
            if (humanCheckPromiseRef.current === verification) humanCheckPromiseRef.current = null;
        });
        return verification;
    };

    const handleCreateAccount = async (event) => {
        event.preventDefault();
        setHasProblem(false);

        if (!isAgreed) {
            setHasProblem(true);
            setProblemText('Вы должны согласиться с условиями политики конфиденциальности и публичной оферты');
            return;
        }
        if (password !== confirmPassword) {
            setHasProblem(true);
            setProblemText('Пароли не совпадают');
            return;
        }
        if (password.length < 8) {
            setHasProblem(true);
            setProblemText('Пароль слишком короткий');
            return;
        }

        let token = currentVerifiedToken();
        if (!token) {
            submitAfterChallengeRef.current = true;
            token = await handleHumanCheck();
        }
        if (token) {
            submitAfterChallengeRef.current = false;
            setSubmitting(true);
            await performRegistration(token);
            setSubmitting(false);
        }
    };

    const refreshCaptcha = async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            await fetchChallenge(controller.signal);
        } catch (error) {
            if (error.name !== 'AbortError') setCaptchaError('Не удалось обновить картинку');
        }
    };

    const handleModalCaptchaSubmit = async () => {
        if (!captchaInput.trim()) {
            setCaptchaError('Введите код с картинки');
            return;
        }
        setCaptchaLoading(true);
        setCaptchaError('');
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const accepted = await verifyVisualCaptcha(captcha.token, captchaInput, controller.signal);
            if (!accepted) {
                await fetchChallenge(controller.signal);
                setCaptchaError('Не совпало. Дадим картинку попроще');
                return;
            }
            const expiresAt = Date.now() + ((captcha.expiresInSeconds || 300) * 1000);
            verifiedTokenRef.current = { token: captcha.token, expiresAt };
            captchaRef.current = null;
            setHumanCheckState('verified');
            setCaptchaModalOpen(false);
            setCaptcha(null);
            if (submitAfterChallengeRef.current) {
                submitAfterChallengeRef.current = false;
                setSubmitting(true);
                await performRegistration(captcha.token);
                setSubmitting(false);
            }
        } catch (error) {
            if (error.name !== 'AbortError') setCaptchaError('Проверка недоступна. Попробуйте ещё раз');
        } finally {
            setCaptchaLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="left-section">
                <div className="placeholder-container">
                    <img src={placeholderImage} alt="" className="placeholder-image" />
                </div>
            </div>

            <div className="right-section">
                <a href="/"><img className="login-logo" src={logo} alt="Animix" /></a>
                <form className="login-form" onSubmit={handleCreateAccount}>
                    <h1 className="auth-title">Регистрация</h1>
                    {hasProblem && <p className="banner">{problemText}</p>}
                    <input type="text" placeholder="Логин" value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="username" />
                    <input type="password" placeholder="Пароль" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
                    <input type="password" placeholder="Повтор" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" />
                    <input
                        className="registration-honeypot"
                        type="text"
                        name="website"
                        tabIndex="-1"
                        autoComplete="off"
                        aria-hidden="true"
                        onChange={(event) => { honeypotRef.current = event.target.value; }}
                    />

                    <div className="checkbox-container">
                        <input
                            type="checkbox"
                            id="terms-checkbox"
                            checked={isAgreed}
                            onChange={(event) => setIsAgreed(event.target.checked)}
                        />
                        <label htmlFor="terms-checkbox">Согласен(-на) с политикой конфиденциальности, офертой</label>
                    </div>

                    <button
                        type="button"
                        className={`human-check human-check--${humanCheckState}`}
                        role="checkbox"
                        aria-checked={humanCheckState === 'verified'}
                        disabled={humanCheckState === 'checking' || submitting}
                        onClick={() => {
                            submitAfterChallengeRef.current = false;
                            handleHumanCheck();
                        }}
                    >
                        <span className="human-check-box" aria-hidden="true">
                            {humanCheckState === 'verified' ? '✓' : ''}
                        </span>
                        <span className="human-check-copy">
                            <strong>{humanCheckState === 'checking' ? 'Проверяем…' : humanCheckState === 'verified' ? 'Готово' : 'Я не робот'}</strong>
                            <small>Защищено Animix</small>
                        </span>
                        <span className="human-check-shield" aria-hidden="true">A</span>
                    </button>
                    {captchaError && !captchaModalOpen && <div className="human-check-error">{captchaError}</div>}

                    <button type="submit" className="button-login" disabled={submitting}>
                        {submitting ? 'Проверяем…' : 'Создать аккаунт'}
                    </button>
                    <button type="button" className="button-sec login" onClick={() => navigate('/login')}>Войти</button>
                    <p className="privacy">Используя сайт, вы соглашаетесь с нашей политикой конфиденциальности</p>
                </form>
            </div>

            <CaptchaModal
                isOpen={captchaModalOpen}
                captchaImage={captcha?.imageBase64 || ''}
                captchaInput={captchaInput}
                onInputChange={(event) => setCaptchaInput(event.target.value)}
                onRefresh={refreshCaptcha}
                onSubmit={handleModalCaptchaSubmit}
                onClose={() => {
                    submitAfterChallengeRef.current = false;
                    setCaptchaModalOpen(false);
                }}
                loading={captchaLoading}
                error={captchaError}
            />
        </div>
    );
};

export default RegisterPage;
